import i18n from "../config/i18n";
// API Response Types
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface ApiError {
  message: string;
  code?: string;
  type?: string;
  status?: number;
}

// Pagination
export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

// User Types
export interface User {
  id: string;
  email: string;
  username: string;
  role: "USER" | "ADMIN";
  subscriptionStatus: "FREE" | "PREMIUM" | "TRIAL";
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

// Auth Types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
}

export interface AuthResponse {
  user: User;
  token: string;
  refreshToken: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

// Text Types
export interface Text {
  id: string;
  title: string;
  content: string;
  language: string;
  level: string;
  difficulty?: number;
  tags?: string[];
  isPublic: boolean;
  freeUserAccess: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTextRequest {
  topic: string;
  level: string;
  textSize: "SHORT" | "MEDIUM" | "LONG";
  languages: string[];
  isPublic: boolean;
  freeUserAccess: boolean;
}

// Email Verification
export interface EmailVerificationRequest {
  token: string;
}

export interface ResendVerificationRequest {
  email: string;
}

// Helper function to convert error to ApiError
export function toApiError(error: unknown): ApiError {
  // Попытка сузить как Axios-совместимый ответ
  if (typeof error === "object" && error !== null) {
    const maybeAxios = error as {
      response?: { data?: unknown; status?: number };
    }; // narrowed external library shape
    if (maybeAxios.response?.data) {
      const data = maybeAxios.response.data as Record<string, unknown>;
      return {
        message:
          typeof data.message === "string"
            ? data.message
            : i18n.t("common:errors.anErrorOccurred"),
        code: typeof data.code === "string" ? data.code : undefined,
        type: typeof data.type === "string" ? data.type : undefined,
        status:
          typeof maybeAxios.response.status === "number"
            ? maybeAxios.response.status
            : 500,
      };
    }
    const maybeMessage = (error as { message?: unknown; status?: unknown })
      .message;
    if (typeof maybeMessage === "string") {
      return {
        message: maybeMessage,
        status:
          typeof (error as { status?: unknown }).status === "number"
            ? (error as { status: number }).status
            : 500,
      };
    }
  }
  return {
    message: i18n.t("common:interface.anUnexpectedErrorOccurred"),
    status: 500,
  };
}
